import PropTypes from 'prop-types';
import React, { Component } from 'react';

import { DropTarget } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';

import './ListView.less';
import 'react-virtualized/styles.css';
// TBD individual imports from 'react-virtualized' to decrease bundle size?
// ex. import Table from 'react-virtualized/dist/commonjs/Table'
import { Table, AutoSizer, SortDirection } from 'react-virtualized';

import { Empty, Typography, notification } from 'antd';
import { EyeInvisibleTwoTone } from '@ant-design/icons';

import ContextMenu from '../ContextMenu';

import Row from './Row.react';
import ScrollOnMouseOut from '../ScrollOnMouseOut';
import { range } from 'lodash';
import detectIt from 'detect-it';
import rawToReactElement from '../raw-to-react-element';
import WithSelection from './withSelectionHOC';
import { isDef } from './utils';
import { ItemTypes } from '../../../../constants/constants';

import { fileOwnerTypes } from '../../../../../../shared/constants/enums';
import api from '../../../../s3-connector/api';
import { getFilesWebkitDataTransferItems, getDirTreeFromFilePaths, getGroupedFilesByFolder } from '../../../../../../shared/utils/files';
import { showRenameSelectedFilesModal } from '../../../../../../shared/utils/files/upload-helper';

//used for horizontal scroll
const TOTAL_COLUMNS_WIDTH = 1150;
const ROW_HEIGHT = 38;
const HEADER_HEIGHT = 38;
const SCROLL_STRENGTH = 80;
const HAS_TOUCH = detectIt.deviceType === 'hasTouch';

const propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      type: PropTypes.string,
      title: PropTypes.string,
      size: PropTypes.number,
      modifyDate: PropTypes.number,
    }),
  ),
  layout: PropTypes.func,
  layoutOptions: PropTypes.object,
  selection: PropTypes.arrayOf(PropTypes.string),
  loading: PropTypes.bool,
  sortBy: PropTypes.string,
  sortDirection: PropTypes.string,
  onRowClick: PropTypes.func,
  onRowRightClick: PropTypes.func,
  onRowDoubleClick: PropTypes.func,
  onScroll: PropTypes.func,
  onSelection: PropTypes.func,
  onSort: PropTypes.func,
  onMoveItems: PropTypes.func,
  onKeyDown: PropTypes.func,
  onRef: PropTypes.func,
  isOnCertsPage: PropTypes.bool,
  handleResizeTable: PropTypes.func,
  handleColumnResize: PropTypes.func,
  totalColumnWidth: PropTypes.number,
  columnWidths: PropTypes.object,
};
const defaultProps = {
  items: [],
  layout: () => [],
  layoutOptions: {},
  selection: [],
  loading: false,
  sortBy: 'title',
  sortDirection: SortDirection.ASC,
  onRowClick: () => {},
  onRowRightClick: () => {},
  onRowDoubleClick: () => {},
  onScroll: () => {},
  onSelection: () => {},
  onSort: () => {},
  onMoveItems: () => {},
  onKeyDown: () => {},
  onRef: () => {},
  isOnCertsPage: false,
  handleResizeTable: () => {},
  handleColumnResize: () => {},
};

class ListView extends Component {
  state = {
    scrollToIndex: 0,
    clientHeight: 0,
    scrollTop: 0,
    scrollHeight: 0,
  };

  shouldComponentUpdate(nextProps, nextState) {
    if (this.props.loading !== nextProps.loading) {
      // Force recalculate scrollHeight for appropriate handle "PageUp, PageDown, Home, End", etc. keys
      this.setState({ scrollHeight: nextProps.items.length * ROW_HEIGHT });
    }
    return true;
  }

  scrollToIndex = (index) => {
    this.setState({ scrollToIndex: index });
  };

  handleScrollTop = (scrollTop) => this.setState({ scrollTop });

  handleScroll = ({ clientHeight, scrollHeight, scrollTop }) => {
    this.props.onScroll({ clientHeight, scrollHeight, scrollTop });
    this.setState({
      ...(isDef(clientHeight) && { clientHeight }),
      ...(isDef(scrollHeight) && { scrollHeight }),
      ...(isDef(scrollTop) && { scrollTop }),
    });
  };

  handlePageUp = (_) => {
    const { scrollTop } = this.state;
    const newScrollTop = scrollTop - SCROLL_STRENGTH < 0 ? 0 : scrollTop - SCROLL_STRENGTH;
    this.handleScrollTop(newScrollTop);
  };

  handlePageDown = (_) => {
    const { scrollTop, scrollHeight, clientHeight } = this.state;
    const newScrollTop = scrollTop + SCROLL_STRENGTH > scrollHeight - clientHeight ? scrollHeight - clientHeight : scrollTop + SCROLL_STRENGTH;
    this.handleScrollTop(newScrollTop);
  };

  handleHome = (_) => this.handleScrollTop(0);

  handleEnd = (_) => {
    // Scroll to the first item
    const { clientHeight, scrollHeight } = this.state;
    const newScrollTop = scrollHeight - clientHeight;
    this.handleScrollTop(newScrollTop);
  };

  handleKeyDown = (e) => {
    e.preventDefault();

    this.props.onKeyDown(e);

    if (e.which === 33) {
      // PageUp
      this.handlePageUp();
    }

    if (e.which === 34) {
      // PageDown
      this.handlePageDown();
    }

    if (e.which === 36) {
      // Home
      this.handleHome();
    }

    if (e.which === 35) {
      // End
      this.handleEnd();
    }
  };

  handleSort = ({ sortBy, sortDirection }) => {
    this.props.onSort({ sortBy, sortDirection });
  };

  handleRowClick = ({ event, index, rowData }) => {
    this.props.onRowClick({ event, index, rowData });
  };

  handleRowRightClick = ({ event, index, rowData }) => {
    this.props.onRowRightClick({ event, index, rowData });
  };

  handleRowDoubleClick = ({ event, index, rowData }) => {
    this.props.onRowDoubleClick({ event, index, rowData });
  };

  handleSelection = ({ selection, scrollToIndex }) => {
    this.props.onSelection(selection);
    this.scrollToIndex(scrollToIndex);
  };

  handleMoveItems = (sourceItemIds, targetItemId) => {
    this.props.onMoveItems(sourceItemIds, targetItemId);
  };

  handleUploadDropedFiles = (targetItemId, files, navigateDirId, filesToUploadCount) => {
    this.props.onUploadDropedFiles(targetItemId, files, navigateDirId, filesToUploadCount);
  };

  render() {
    const {
      items,
      layout,
      layoutOptions,
      loading,
      onRef,
      sortBy,
      sortDirection,
      connectDropTarget,
      isOver,
      isOverCurrent,
      canDrop,
      permissions,
      rootContainer,
      rowContextMenuItems,
      filesViewContextMenuItems,
      isOnCertsPage,
      handleResizeTable,
      handleColumnResize,
    } = this.props;

    const { clientHeight, scrollTop, scrollHeight, scrollToIndex } = this.state;

    let itemsToRender = null;
    if (loading && this.containerHeight) {
      // Generate items for "loading placeholder"
      const itemsCount = Math.floor(this.containerHeight / ROW_HEIGHT - 1);
      itemsToRender = range(itemsCount).map((_) => ({}));
    } else {
      itemsToRender = items;
    }

    return (
      <AutoSizer onResize={this.props.handleResizeTable}>
        {({ width, height }) =>
          (this.containerHeight = height) && (
            <WithSelection
              items={itemsToRender}
              onKeyDown={this.handleKeyDown}
              onSelection={this.handleSelection}
              onRowClick={this.handleRowClick}
              onRowRightClick={this.handleRowRightClick}
              onRowDoubleClick={this.handleRowDoubleClick}
              selection={this.props.selection}
              onRef={onRef}
            >
              {({ onRowClick, onRowRightClick, onRowDoubleClick, selection, lastSelected }) =>
                connectDropTarget(
                  <div className={`oc-fm--list-view`}>
                    <ContextMenu
                      rootContainer={rootContainer}
                      items={filesViewContextMenuItems}
                      permissions={permissions}
                      cssClass={isOnCertsPage ? 'oc-fm-list-view-dropdown-context' : ''}
                    >
                      <ScrollOnMouseOut
                        onCursorAbove={this.handleScrollTop}
                        onCursorBellow={this.handleScrollTop}
                        clientHeight={clientHeight}
                        scrollHeight={scrollHeight}
                        scrollTop={scrollTop}
                        topCaptureOffset={40}
                        bottomCaptureOffset={0}
                        className={`${isOver && isOverCurrent && canDrop ? 'oc-fm--list-view-drag-over' : ''}`}
                        style={{
                          width: `${width - (isOver && isOverCurrent && canDrop ? 4 : 0)}px`,
                          height: `${height - (isOver && isOverCurrent && canDrop ? 4 : 0)}px`,
                        }}
                      >
                        <div style={{ width: '100%', height: '100%', overflowX: 'auto' }}>
                          <Table
                            width={this.props.totalColumnWidth}
                            height={height - ROW_HEIGHT / 2}
                            rowCount={itemsToRender.length}
                            rowGetter={({ index }) => itemsToRender[index]}
                            rowHeight={ROW_HEIGHT}
                            headerHeight={HEADER_HEIGHT}
                            columnWidths={this.props.columnWidths}
                            onColumnResize={this.props.handleColumnResize}
                            className="oc-fm--list-view__table"
                            gridClassName={'oc-fm--list-view__grid' + (isOnCertsPage ? ' oc-fm--list__grid-additional-scroll' : '')}
                            overscanRowCount={10}
                            onScroll={this.handleScroll}
                            scrollToIndex={scrollToIndex}
                            scrollTop={scrollTop}
                            sortBy={sortBy}
                            sortDirection={sortDirection}
                            rowRenderer={(rowProps) => (
                              <Row
                                selection={selection}
                                lastSelected={lastSelected}
                                isSystemFolderSelected={
                                  selection && items.filter((x) => selection.some((s) => s == x.id)).some((x) => x.isSystemFolder)
                                }
                                hasTouch={HAS_TOUCH}
                                onMoveItems={this.handleMoveItems}
                                onUploadDropedFiles={this.handleUploadDropedFiles}
                                rowContextMenuItems={rowContextMenuItems}
                                permissions={permissions}
                                {...rowProps}
                                ownerType={this.props.ownerType}
                                rootContainer={rootContainer}
                              />
                            )}
                            noRowsRenderer={() => (
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '100%',
                                  height: '100%',
                                }}
                              >
                                <Empty
                                  image={false}
                                  imageStyle={{
                                    width: '0',
                                    height: '0',
                                    padding: '0',
                                    margin: '0',
                                  }}
                                  description={
                                    <span
                                      style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '100%',
                                        height: '100%',
                                      }}
                                    >
                                      <EyeInvisibleTwoTone style={{ fontSize: '32px' }} />
                                      <span style={{ fontSize: '24px' }}>
                                        <Typography.Text>Nothing to show</Typography.Text>
                                      </span>
                                      <span>
                                        <Typography.Text>Use toolbar or context menu to perform available actions</Typography.Text>
                                      </span>
                                    </span>
                                  }
                                />
                              </div>
                            )}
                            onRowClick={onRowClick}
                            onRowRightClick={onRowRightClick}
                            onRowDoubleClick={onRowDoubleClick}
                          >
                            {layout({
                              ...layoutOptions,
                              loading,
                              width,
                              height,
                              showAllInfo: isOnCertsPage ? true : false,
                              columnWidths: this.props.columnWidths,
                              onColumnResize: this.props.handleColumnResize,
                              onSort: this.handleSort,
                            }).map((rawLayoutChild, i) => rawToReactElement(rawLayoutChild, i))}
                          </Table>
                        </div>
                      </ScrollOnMouseOut>
                    </ContextMenu>
                    {this.props.children}
                  </div>,
                )
              }
            </WithSelection>
          )
        }
      </AutoSizer>
    );
  }
}

ListView.propTypes = propTypes;
ListView.defaultProps = defaultProps;

const itemDropTarget = {
  canDrop(props, monitor) {
    if (+props.ownerType === fileOwnerTypes.InsuredDefaultFolderStructure || +props.ownerType === fileOwnerTypes.PolicyDefaultFolderStructure) {
      return false;
    }

    const sourceItem = monitor.getItem();
    const targetItem = props;
    if (
      sourceItem.files &&
      sourceItem.files.some((file) => file.size === 0 && file.type === '') &&
      (targetItem.currentResourceId.endsWith('_googledrive') || targetItem.currentResourceId.endsWith('_onedrive'))
    ) {
      return false;
    }

    return (
      props.currentResourceType === ItemTypes.Directory &&
      props.currentResourceCapability.canAddChildren &&
      (props.permissions ? props.permissions.filePermissions.canAdd : false)
    );
  },

  drop(props, monitor, component) {
    if (monitor.didDrop()) {
      return;
    }

    const itemType = monitor.getItemType();

    if (itemType === NativeTypes.FILE) {
      const targetItemId = props.currentResourceId;
      const items = monitor.getItem().items;
      const droppedFiles = monitor.getItem().files;
      const isAllMsgFiles = droppedFiles.every((f) => f.name.endsWith('.msg') && f.type === '');
      if (targetItemId.endsWith('_googledrive') || targetItemId.endsWith('_onedrive') || isAllMsgFiles) {
        if (targetItemId && droppedFiles) {
          props.onUploadDropedFiles(targetItemId, droppedFiles, null, null);
        }
      } else {
        getFilesWebkitDataTransferItems(items).then((files) => {
          const paths = files.map((a) => a.filepath);
          const dirTree = getDirTreeFromFilePaths(paths);

          const createFolderTree = (files, targetItemId, dirTree) => {
            api.createFolderTree(targetItemId, dirTree).then((createFolderTreeResut) => {
              const groupedFilesByFolder = getGroupedFilesByFolder(createFolderTreeResut.data, files);
              for (const folderId in groupedFilesByFolder) {
                let filesByFolder = groupedFilesByFolder[folderId].files;
                if (folderId && filesByFolder) {
                  props.onUploadDropedFiles(folderId, filesByFolder, targetItemId, filesByFolder.length);
                }
              }
            });
          };

          const rootfolderNames = _.uniqBy(dirTree.filter((f) => !f.isFile).map((d) => d.name));
          if (rootfolderNames.length) {
            api.getFilesInDestinationFolder(null, rootfolderNames, targetItemId).then((existingFiles) => {
              const existingfileNames = _.uniqBy(existingFiles, 'name').map((x) => x.name);
              if (existingfileNames.length) {
                showRenameSelectedFilesModal(existingfileNames, props.rootContainer, () => {
                  createFolderTree(files, targetItemId, dirTree);
                });
              } else {
                createFolderTree(files, targetItemId, dirTree);
              }
            });
          } else {
            createFolderTree(files, targetItemId, dirTree);
          }
        });
      }
    }

    return { moved: true };
  },
};

export default DropTarget(['file_or_folder', NativeTypes.FILE], itemDropTarget, (connect, monitor) => ({
  connectDropTarget: connect.dropTarget(),
  isOver: monitor.isOver(),
  isOverCurrent: monitor.isOver({ shallow: true }),
  canDrop: monitor.canDrop(),
  itemType: monitor.getItemType(),
}))(ListView);
